import { API } from "@/config/apiBase";
import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Upload, Check, X, Search, Users, Mail, Phone, 
  Calendar, ChevronRight, AlertCircle, RefreshCw
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";

const GoogleContactsImport = ({ onImportComplete, trigger }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState("initial"); // initial, loading, select, importing, success, error
  const [contacts, setContacts] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState("");
  const [connectionStatus, setConnectionStatus] = useState("unknown"); // unknown, connected, disconnected, expired, revoked
  const [importResult, setImportResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Check Google Contacts connection status on mount
  useEffect(() => {
    checkConnectionStatus();
  }, []);

  // Check for OAuth callback parameters
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const success = params.get("google_contacts_success");
    const error = params.get("google_contacts_error");

    if (success === "true") {
      setConnectionStatus("connected");
      toast.success("Google Contacts connected successfully!");
      // Clean up URL
      window.history.replaceState({}, "", window.location.pathname);
      // Auto-open the import dialog
      setIsOpen(true);
      fetchContacts();
    } else if (error) {
      // Map technical errors to user-friendly messages
      const friendlyError = getErrorMessage(error);
      toast.error(friendlyError);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  // Map technical error codes to user-friendly messages
  const getErrorMessage = (errorCode) => {
    const errorMessages = {
      "access_denied": "You declined the Google connection. You can try again anytime.",
      "invalid_state": "The connection request expired. Please try again.",
      "state_expired": "The connection request expired. Please try again.",
      "token_failed": "We couldn't connect to Google. Please try again or contact support.",
      "missing_params": "Something went wrong. Please try again.",
      "unknown": "We couldn't connect to Google. Please try again or contact support."
    };
    return errorMessages[errorCode] || errorMessages["unknown"];
  };

  const checkConnectionStatus = async () => {
    try {
      const response = await axios.get(`${API}/google/contacts/status`);
      if (response.data.connected) {
        setConnectionStatus("connected");
      } else if (response.data.expired) {
        setConnectionStatus("expired");
      } else if (response.data.revoked) {
        setConnectionStatus("revoked");
      } else {
        setConnectionStatus("disconnected");
      }
    } catch (error) {
      console.error("Failed to check Google status:", error);
      setConnectionStatus("disconnected");
    }
  };

  const handleConnect = async () => {
    setIsLoading(true);
    setErrorMessage("");
    try {
      const response = await axios.get(`${API}/google/contacts/auth-url`);
      // Redirect to Google OAuth
      window.location.href = response.data.auth_url;
    } catch (error) {
      console.error("Failed to get auth URL:", error);
      const message = error.response?.status === 503 
        ? "Google import is not available right now. Please try again later."
        : "We couldn't connect to Google. Please try again or contact support.";
      setErrorMessage(message);
      toast.error(message);
      setIsLoading(false);
    }
  };

  const fetchContacts = async () => {
    setStep("loading");
    setErrorMessage("");
    try {
      const response = await axios.get(`${API}/google/contacts/list`);
      setContacts(response.data.contacts);
      setStep("select");
    } catch (error) {
      console.error("Failed to fetch contacts:", error);
      if (error.response?.status === 401) {
        // Token expired or revoked
        setConnectionStatus("expired");
        setStep("initial");
        setErrorMessage("Your Google connection has expired. Please reconnect to continue.");
        toast.error("Google authorization expired. Please reconnect.");
      } else {
        setErrorMessage("We couldn't fetch your contacts. Please try again or contact support.");
        toast.error("Failed to fetch contacts from Google.");
        setStep("initial");
      }
    }
  };

  const handleSelectAll = () => {
    if (selectedIds.size === filteredContacts.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredContacts.map((_, i) => i)));
    }
  };

  const handleToggleContact = (index) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedIds(newSelected);
  };

  const handleImport = async () => {
    if (selectedIds.size === 0) {
      toast.error("Please select at least one contact");
      return;
    }

    setStep("importing");
    const selectedContacts = filteredContacts.filter((_, i) => selectedIds.has(i));

    try {
      const response = await axios.post(`${API}/google/contacts/import`, {
        contacts: selectedContacts
      });
      
      setImportResult(response.data);
      setStep("success");
      
      if (onImportComplete) {
        onImportComplete(response.data);
      }
    } catch (error) {
      console.error("Import failed:", error);
      toast.error(error.response?.data?.detail || "Failed to import contacts");
      setStep("select");
    }
  };

  const handleDisconnect = async () => {
    try {
      await axios.delete(`${API}/google/contacts/disconnect`);
      setConnectionStatus("disconnected");
      setContacts([]);
      setSelectedIds(new Set());
      setErrorMessage("");
      toast.success("Google Contacts disconnected");
    } catch (error) {
      console.error("Disconnect failed:", error);
      toast.error("Failed to disconnect. Please try again.");
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setStep("initial");
    setSearchQuery("");
    setSelectedIds(new Set());
    setImportResult(null);
    setErrorMessage("");
  };

  // Helper to determine if connected
  const isConnected = connectionStatus === "connected";
  const needsReconnect = connectionStatus === "expired" || connectionStatus === "revoked";

  const filteredContacts = contacts.filter(contact => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      contact.full_name?.toLowerCase().includes(query) ||
      contact.emails?.some(e => e.toLowerCase().includes(query)) ||
      contact.phones?.some(p => p.includes(query))
    );
  });

  // Custom trigger or default button
  const TriggerButton = trigger ? (
    <div onClick={() => setIsOpen(true)}>{trigger}</div>
  ) : (
    <Button
      variant="outline"
      className="w-full border-[#F4F1EA] rounded-xl h-12 justify-start gap-3"
      onClick={() => setIsOpen(true)}
      data-testid="import-google-contacts-button"
    >
      <svg className="w-5 h-5" viewBox="0 0 24 24">
        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
      </svg>
      Import from Google Contacts
    </Button>
  );

  return (
    <>
      {TriggerButton}

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Fraunces, serif' }}>
              <svg className="w-6 h-6" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Import Google Contacts
            </DialogTitle>
            <DialogDescription>
              We'll never message anyone without your permission.
            </DialogDescription>
          </DialogHeader>

          <AnimatePresence mode="wait">
            {/* Initial / Connect State */}
            {step === "initial" && (
              <motion.div
                key="initial"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="py-8 text-center"
              >
                {/* Error message display */}
                {errorMessage && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600">
                    <AlertCircle className="w-4 h-4 inline mr-2" />
                    {errorMessage}
                  </div>
                )}

                {needsReconnect ? (
                  // Expired/Revoked - Show Reconnect
                  <>
                    <div className="w-16 h-16 bg-[#F2CC8F]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                      <AlertCircle className="w-8 h-8 text-[#F2CC8F]" />
                    </div>
                    <h3 className="text-lg font-medium text-[#3D405B] mb-2">
                      Reconnect Google
                    </h3>
                    <p className="text-sm text-[#6D6F7C] mb-6 max-w-sm mx-auto">
                      Your Google connection has expired. Please reconnect to import your contacts.
                    </p>
                    <Button
                      onClick={handleConnect}
                      disabled={isLoading}
                      className="bg-[#E07A5F] hover:bg-[#D0694E] text-white rounded-full px-8"
                      data-testid="reconnect-google-button"
                    >
                      {isLoading ? (
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <RefreshCw className="w-4 h-4 mr-2" />
                      )}
                      Reconnect Google
                    </Button>
                  </>
                ) : !isConnected ? (
                  // Not connected - Show Connect
                  <>
                    <div className="w-16 h-16 bg-[#F4F1EA] rounded-full flex items-center justify-center mx-auto mb-4">
                      <Users className="w-8 h-8 text-[#E07A5F]" />
                    </div>
                    <h3 className="text-lg font-medium text-[#3D405B] mb-2">
                      Connect Your Google Account
                    </h3>
                    <p className="text-sm text-[#6D6F7C] mb-6 max-w-sm mx-auto">
                      Import contacts from your Google account to quickly add people you want to stay in touch with.
                    </p>
                    <Button
                      onClick={handleConnect}
                      disabled={isLoading}
                      className="bg-[#E07A5F] hover:bg-[#D0694E] text-white rounded-full px-8"
                      data-testid="connect-google-button"
                    >
                      {isLoading ? (
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
                          <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                          <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        </svg>
                      )}
                      Connect with Google
                    </Button>
                  </>
                ) : (
                  // Connected - Show Import
                  <>
                    <div className="w-16 h-16 bg-[#81B29A]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Check className="w-8 h-8 text-[#81B29A]" />
                    </div>
                    <h3 className="text-lg font-medium text-[#3D405B] mb-2">
                      Google Contacts Connected
                    </h3>
                    <p className="text-sm text-[#6D6F7C] mb-6">
                      Ready to import your contacts.
                    </p>
                    <div className="flex gap-3 justify-center">
                      <Button
                        onClick={fetchContacts}
                        className="bg-[#E07A5F] hover:bg-[#D0694E] text-white rounded-full px-6"
                        data-testid="fetch-contacts-button"
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        Import Contacts
                      </Button>
                      <Button
                        variant="outline"
                        onClick={handleDisconnect}
                        className="border-[#F4F1EA] rounded-full"
                      >
                        Disconnect
                      </Button>
                    </div>
                  </>
                )}
              </motion.div>
            )}

            {/* Loading State */}
            {step === "loading" && (
              <motion.div
                key="loading"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="py-12 text-center"
              >
                <RefreshCw className="w-8 h-8 text-[#E07A5F] animate-spin mx-auto mb-4" />
                <p className="text-[#6D6F7C]">Fetching your contacts...</p>
              </motion.div>
            )}

            {/* Contact Selection */}
            {step === "select" && (
              <motion.div
                key="select"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="flex flex-col flex-1 min-h-0"
              >
                {/* Search and Select All */}
                <div className="flex items-center gap-3 mb-4">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
                    <Input
                      placeholder="Search contacts..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 border-[#F4F1EA] rounded-xl"
                      data-testid="search-contacts-input"
                    />
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleSelectAll}
                    className="border-[#F4F1EA] rounded-lg whitespace-nowrap"
                  >
                    {selectedIds.size === filteredContacts.length ? "Deselect All" : "Select All"}
                  </Button>
                </div>

                {/* Contact Stats */}
                <div className="flex items-center justify-between text-sm text-[#6D6F7C] mb-3">
                  <span>{filteredContacts.length} contacts found</span>
                  <span className="text-[#E07A5F] font-medium">{selectedIds.size} selected</span>
                </div>

                {/* Contact List */}
                <div className="flex-1 overflow-y-auto border border-[#F4F1EA] rounded-xl divide-y divide-[#F4F1EA] min-h-[200px] max-h-[300px]">
                  {filteredContacts.length === 0 ? (
                    <div className="p-8 text-center text-[#6D6F7C]">
                      {searchQuery ? "No contacts match your search" : "No contacts found"}
                    </div>
                  ) : (
                    filteredContacts.map((contact, index) => (
                      <div
                        key={index}
                        className={`flex items-center gap-3 p-3 hover:bg-[#F4F1EA]/50 cursor-pointer transition-colors ${
                          selectedIds.has(index) ? "bg-[#E07A5F]/5" : ""
                        }`}
                        onClick={() => handleToggleContact(index)}
                      >
                        <Checkbox
                          checked={selectedIds.has(index)}
                          onCheckedChange={() => handleToggleContact(index)}
                          className="data-[state=checked]:bg-[#E07A5F] data-[state=checked]:border-[#E07A5F]"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-[#3D405B] truncate">{contact.full_name}</p>
                          <div className="flex items-center gap-3 text-xs text-[#6D6F7C]">
                            {contact.emails?.[0] && (
                              <span className="flex items-center gap-1 truncate">
                                <Mail className="w-3 h-3" />
                                {contact.emails[0]}
                              </span>
                            )}
                            {contact.phones?.[0] && (
                              <span className="flex items-center gap-1">
                                <Phone className="w-3 h-3" />
                                {contact.phones[0]}
                              </span>
                            )}
                          </div>
                        </div>
                        {contact.birthday && (
                          <Badge variant="outline" className="text-xs border-[#F4F1EA]">
                            <Calendar className="w-3 h-3 mr-1" />
                            Birthday
                          </Badge>
                        )}
                      </div>
                    ))
                  )}
                </div>

                {/* Import Button */}
                <div className="flex justify-end gap-3 mt-4 pt-4 border-t border-[#F4F1EA]">
                  <Button
                    variant="outline"
                    onClick={handleClose}
                    className="border-[#F4F1EA] rounded-full"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleImport}
                    disabled={selectedIds.size === 0}
                    className="bg-[#E07A5F] hover:bg-[#D0694E] text-white rounded-full"
                    data-testid="import-selected-button"
                  >
                    Import {selectedIds.size} Contact{selectedIds.size !== 1 ? "s" : ""}
                  </Button>
                </div>
              </motion.div>
            )}

            {/* Importing State */}
            {step === "importing" && (
              <motion.div
                key="importing"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="py-12 text-center"
              >
                <RefreshCw className="w-8 h-8 text-[#E07A5F] animate-spin mx-auto mb-4" />
                <p className="text-[#6D6F7C]">Importing contacts...</p>
              </motion.div>
            )}

            {/* Success State */}
            {step === "success" && importResult && (
              <motion.div
                key="success"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                className="py-8 text-center"
              >
                <div className="w-16 h-16 bg-[#81B29A]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check className="w-8 h-8 text-[#81B29A]" />
                </div>
                <h3 className="text-lg font-medium text-[#3D405B] mb-2">
                  Import Complete!
                </h3>
                <p className="text-[#6D6F7C] mb-6">
                  Imported {importResult.imported_count} contact{importResult.imported_count !== 1 ? "s" : ""}.
                  {importResult.skipped_count > 0 && (
                    <span className="block text-sm mt-1">
                      Skipped {importResult.skipped_count} duplicate{importResult.skipped_count !== 1 ? "s" : ""}.
                    </span>
                  )}
                </p>
                <Button
                  onClick={handleClose}
                  className="bg-[#E07A5F] hover:bg-[#D0694E] text-white rounded-full px-8"
                >
                  Done
                </Button>
              </motion.div>
            )}
          </AnimatePresence>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default GoogleContactsImport;
